import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

const BASE = "/api";

export type AnalyticsDays = 7 | 30 | 90;
export type TopPiecesSortBy = "impressions" | "engagement" | "engagementRate";

export const analyticsApi = {
  fetchOverview: async (brandId: string, days: AnalyticsDays) => {
    const r = await fetch(
      `${BASE}/brands/${brandId}/analytics/overview?days=${days}`,
      { credentials: "include" },
    );
    if (!r.ok) throw new Error("Failed to load analytics overview");
    return r.json();
  },
  fetchTopPieces: async (
    brandId: string,
    days: number,
    sortBy: TopPiecesSortBy,
    limit?: number,
  ) => {
    const params = new URLSearchParams({ days: String(days), sortBy });
    if (limit !== undefined) params.set("limit", String(limit));
    const r = await fetch(
      `${BASE}/brands/${brandId}/analytics/top-pieces?${params.toString()}`,
      { credentials: "include" },
    );
    if (!r.ok) throw new Error("Failed to load top pieces");
    return r.json();
  },
  fetchPieceTimeline: async (brandId: string, pieceId: string) => {
    const r = await fetch(
      `${BASE}/brands/${brandId}/analytics/pieces/${pieceId}/timeline`,
      { credentials: "include" },
    );
    if (!r.ok) throw new Error("Failed to load piece timeline");
    return r.json();
  },
  fetchBestTimes: async (brandId: string, platform?: string) => {
    const url = platform
      ? `${BASE}/brands/${brandId}/analytics/best-times?platform=${encodeURIComponent(platform)}`
      : `${BASE}/brands/${brandId}/analytics/best-times`;
    const r = await fetch(url, { credentials: "include" });
    if (!r.ok) throw new Error("Failed to load best times");
    return r.json();
  },
  fetchSyncLatest: async (brandId: string) => {
    const r = await fetch(`${BASE}/brands/${brandId}/analytics/sync/latest`, {
      credentials: "include",
    });
    if (!r.ok) throw new Error("Failed to load sync status");
    return r.json();
  },
  triggerSync: async (brandId: string) => {
    const r = await fetch(`${BASE}/brands/${brandId}/analytics/sync`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    });
    if (!r.ok) throw new Error("Failed to trigger sync");
    return r.json();
  },
  fetchSyncStatus: async (brandId: string, jobId: string) => {
    const r = await fetch(
      `${BASE}/brands/${brandId}/analytics/sync/${jobId}`,
      { credentials: "include" },
    );
    if (!r.ok) throw new Error("Failed to load sync job status");
    return r.json();
  },
};

// --- TanStack Query hooks ---

export function useAnalyticsOverview(brandId: string, days: AnalyticsDays) {
  return useQuery({
    queryKey: ["analytics", brandId, "overview", days],
    queryFn: () => analyticsApi.fetchOverview(brandId, days),
    enabled: !!brandId,
    staleTime: 5 * 60_000,
  });
}

export function useTopPieces(
  brandId: string,
  days: number,
  sortBy: TopPiecesSortBy,
  limit?: number,
) {
  return useQuery({
    queryKey: ["analytics", brandId, "top-pieces", days, sortBy, limit],
    queryFn: () => analyticsApi.fetchTopPieces(brandId, days, sortBy, limit),
    enabled: !!brandId,
    staleTime: 5 * 60_000,
  });
}

export function usePieceTimeline(brandId: string, pieceId: string | null) {
  return useQuery({
    queryKey: ["analytics", brandId, "piece-timeline", pieceId],
    queryFn: () => analyticsApi.fetchPieceTimeline(brandId, pieceId as string),
    enabled: !!brandId && !!pieceId,
  });
}

export function useBestTimes(brandId: string, platform?: string) {
  return useQuery({
    queryKey: ["analytics", brandId, "best-times", platform ?? null],
    queryFn: () => analyticsApi.fetchBestTimes(brandId, platform),
    enabled: !!brandId,
    staleTime: 5 * 60_000,
  });
}

export function useSyncLatest(brandId: string) {
  return useQuery({
    queryKey: ["analytics", brandId, "sync-latest"],
    queryFn: () => analyticsApi.fetchSyncLatest(brandId),
    enabled: !!brandId,
    refetchInterval: (query) => {
      const data = query.state.data as { inProgress?: boolean } | undefined;
      return data?.inProgress ? 2_000 : 60_000;
    },
  });
}

export function useTriggerSync(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => analyticsApi.triggerSync(brandId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["analytics", brandId, "sync-latest"] });
    },
  });
}

export function useSyncStatus(brandId: string, jobId: string | null) {
  return useQuery({
    queryKey: ["analytics", brandId, "sync-status", jobId],
    queryFn: () => analyticsApi.fetchSyncStatus(brandId, jobId as string),
    enabled: !!brandId && !!jobId,
    refetchInterval: (query) => {
      const data = query.state.data as { status?: string } | undefined;
      if (data?.status === "COMPLETED" || data?.status === "FAILED") {
        return false;
      }
      return 2_000;
    },
  });
}
